// Imports for external libraries go here.
import { FC, useState, useEffect, useRef, useMemo } from 'react';
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { MobileSearchFormContentProps, SearchFormViewProps } from './SearchForm.types';
import { StyledSearchFormMobileWrapperDiv, StyledSearchFormDesktopWrapperDiv } from './SearchForm.styles';
import { DropDownModal, InputTextField, getLocalizedDate } from '@marriott/mi-ui-library';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/src/utils';
import { SearchField, DatePicker } from '../../molecules'; // search form molecules
import {
  ADF_SEARCH_CTA_ENDPOINT_URL,
  DATE_MOBILE_FORMAT_DEFAULT,
  SearchFormActiveState,
  searchFormClickTrackingLoc,
} from '../../constants';
import { SearchPrimaryBlock, SearchFormSubmitButton } from './SearchForm.primary'; //search form top block consist of datepicker and destination molecules
import { SearchSecondaryBlock } from './SearchForm.secondry'; //search from down block
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import clsx from 'clsx';
import { isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import React from 'react';
import { SearchFormState, useSearchFormStore } from '../../modules/store/searchFormStore';
import { useStaticDataContext } from '../../modules/context';
import { getCustomFormattedDate } from '../../modules/utils';
import { FLEXIBLE_FORMAT_YEAR_MONTH } from '../../modules/constants';
import { formatDateStringBasedOnLocale } from '../../utils/src/helper';
import { convertToUpperCase } from '../../utils/src/utils';
import { useConditionalPageContextSERP } from '../../hooks';
import { ConversationalAISearchBanner } from '../../molecules/ConversationalAISearchBanner';
import { MAP_VIEW_SESSION_KEY } from '../../modules/store/store.constants';

const MobileSearchFormContent: FC<MobileSearchFormContentProps> = React.memo(
  ({
    contentType,
    isTabbedSearchForm,
    isDatesSelected,
    isDestinationParsed,
    isDesktopView,
    isTabletView,
    formModalState,
    isSecondarySearchBlock,
    isAvailabilityScenario,
    hideDestination,
    model,
    sessionData,
    setCurrentFormState,
    setMobileFormModalState,
    setIsDestinationParsed,
    setIsDatesSelected,
    setIsWeekendButtonSelected,
    isWeekendButtonSelected,
    bodyRef: _bodyRefProp,
    buttonRef,
    setReadDatesFromStore,
  }) => {
    /**
     * return content as per the required render data
     */

    switch (contentType) {
      case SearchFormActiveState.DESTINATION_STATE:
        /**
         * return destination field if state == destination
         */
        return (
          <SearchField
            onChange={(inputValue: string) => {
              if (!isDatesSelected) {
                setReadDatesFromStore(true);
                setCurrentFormState(SearchFormActiveState.CALENDAR_STATE);
              } else if (isTabbedSearchForm) {
                setCurrentFormState(SearchFormActiveState.DEFAULT_STATE);
              } else {
                setCurrentFormState(SearchFormActiveState.FORM_STATE);
              }
              setMobileFormModalState(true);
              setIsDestinationParsed(inputValue);
            }}
            onCancel={() => {
              setMobileFormModalState(false);
              setCurrentFormState(SearchFormActiveState.CLOSED_STATE);
            }}
            isDesktopView={isDesktopView}
            isModalOpen={true}
            mobileModalHeading={model?.destinationModalHeader}
            mobilePlaceHolderText={model?.placeholderTextMobile}
            blacklist={model?.blacklist}
          />
        );
      case SearchFormActiveState.CALENDAR_STATE:
        /**
         * return destination field if state == calendar
         */
        return (
          <DatePicker
            onDatePickerChange={(isDatesAvailable: boolean) => {
              setMobileFormModalState(true);
              setIsDatesSelected(isDatesAvailable);
              // Don't navigate to destination if hideDestination is true
              !isDestinationParsed && !hideDestination && model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL
                ? setCurrentFormState(SearchFormActiveState.DESTINATION_STATE)
                : isTabbedSearchForm
                ? setCurrentFormState(SearchFormActiveState.DEFAULT_STATE)
                : setCurrentFormState(SearchFormActiveState.FORM_STATE);
              setReadDatesFromStore(true);
            }}
            isModalOpen={true}
            onDatePickerCancel={() => {
              setReadDatesFromStore(true);
              setMobileFormModalState(false);
              setCurrentFormState(SearchFormActiveState.CLOSED_STATE);
            }}
            setDefaultDate={true}
            setIsWeekendButtonSelected={setIsWeekendButtonSelected}
            isWeekendButtonSelected={isWeekendButtonSelected}
          />
        );
      case SearchFormActiveState.FORM_STATE:
        /**
         * return destination field if state == form
         * display whole form with modal
         */
        return (
          <DropDownModal
            applyDefaultHeight={isTabbedSearchForm === true ? true : false}
            className="mobile-search-form"
            role="dialog"
            childrenClassName={'search-form-dropdown'}
            show={formModalState}
            dropdownModalOpenState={true}
            setDropdownModalOpenState={setMobileFormModalState}
            // mobile setting enabled
            mobileTakeOver={true}
            mobileModalHeading={model?.searchLabel}
            mobileHeaderEnabled={true}
            dropdownModalOnCLoseFunc={() => {
              setMobileFormModalState(false);
              // DOM manipulation moved to useEffect that responds to formModalState
              setCurrentFormState(SearchFormActiveState.CLOSED_STATE);
            }}
            customLastElementRef={buttonRef}
          >
            <SearchPrimaryBlock
              isButtonHide={true}
              alertsListArray={model?.alertsListArray}
              isDesktopView={isDesktopView}
              submitAction={model?.submitAction}
              mobileModalHeading={model?.destinationModalHeader}
              mobilePlaceHolderText={model?.placeholderTextDesktop}
              isMobileForm={true}
              baselineComponentTitle={model?.baselineComponentTitle}
              isAvailabilityScenario={isAvailabilityScenario}
              sessionData={sessionData}
              blacklist={model?.blacklist}
              setCurrentFormState={setCurrentFormState}
              isDestinationParsed={isDestinationParsed}
              isDatesSelected={isDatesSelected}
              setIsDatesSelected={setIsDatesSelected}
              setIsDestinationParsed={setIsDestinationParsed}
            />
            {isSecondarySearchBlock && (
              <SearchSecondaryBlock
                isDesktopView={isDesktopView}
                isTabletView={isTabletView}
                submitAction={model?.submitAction}
                isAvailabilityScenario={isAvailabilityScenario}
                sessionData={sessionData}
              />
            )}
            {isTabbedSearchForm !== true && (
              <SearchFormSubmitButton
                findHotelLabel={model?.findHotelLabel}
                submitAction={model?.submitAction}
                lastElementRef={buttonRef}
                isAvailabilityScenario={isAvailabilityScenario}
                sessionData={sessionData}
                setMobileFormModalState={setMobileFormModalState}
              />
            )}
          </DropDownModal>
        );

      /**
       * add any other case if required
       */
      default:
        /**
         * TODO://  need to return some default content
         * will check and update
         */
        return <div></div>;
    }
  }
);

export const SearchFormView = React.memo((props: SearchFormViewProps) => {
  const { model, hideBrandsField, hideDestination, sessionData, currentLocale, isNonStickyVariant } = props;
  const { isBulgariDomain } = useConditionalPageContextSERP();
  const staticDataContext = useStaticDataContext();

  const isDesktopView = useCheckBreakpoint('viewportL');
  const isTabletView = useCheckBreakpoint('viewportM');
  const isAvailabilityScenario = hideDestination && hideBrandsField;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setIsWeekendButtonSelected = useSearchFormStore((state: any) => state.setHotelsSearchformWeekendButtonSelected);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isWeekendButtonSelected = useSearchFormStore((state: any) => state.isHotelsSearchformWeekendButtonSelected);

  // Get destination and dates from store for mobile pills display
  const formDestination = useSearchFormStore((state: SearchFormState) => state.destination);
  const datesState = useSearchFormStore((state: SearchFormState) => state.dates);
  const fromDate = datesState?.fromDate;
  const toDate = datesState?.toDate;
  const flexible = datesState?.flexible;
  const storeDestinationText = formDestination?.displayText || '';

  // Check if sessionData has dates (to identify returning user vs first-time user)
  const cacheDataPresent = !!sessionData?.sessionData?.cacheData?.data;
  const sessionCheckInDate = cacheDataPresent
    ? sessionData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate
    : sessionData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate;

  // State to track if we should read dates from store
  // Initially true if dates exist in session (returning user), false otherwise (first-time user)
  // Set to true when user clicks on date modal
  const [readDatesFromStore, setReadDatesFromStore] = useState(false);

  useEffect(() => {
    setReadDatesFromStore(!!sessionCheckInDate);
  }, [sessionCheckInDate]);

  // Format dates for mobile display (aligned with searchMobile.tsx)
  const mobileDatesDisplayText = useMemo(() => {
    // If readDatesFromStore is false, show placeholder "Add dates"
    if (!readDatesFromStore) {
      return '';
    }

    const locale = staticDataContext?.currentLocale || 'en-US';
    const flexibleDateFormatLabel = staticDataContext?.flexibleDateFormatLabel;
    const mobileDateFormatLabel = staticDataContext?.mobileDateFormatLabel;

    // Determine the displayed date format (same logic as searchMobile.tsx lines 79-91)
    let displayedDateFormat = '';
    if (flexible) {
      displayedDateFormat = flexibleDateFormatLabel ?? FLEXIBLE_FORMAT_YEAR_MONTH;
    } else if (mobileDateFormatLabel && mobileDateFormatLabel !== 'null') {
      displayedDateFormat = mobileDateFormatLabel;
    } else {
      displayedDateFormat = DATE_MOBILE_FORMAT_DEFAULT;
    }

    let displayText = '';

    // Exact dates (not flexible)
    if ((fromDate || toDate) && !flexible) {
      displayText = `${
        fromDate ? getCustomFormattedDate(getLocalizedDate(fromDate, displayedDateFormat, locale), locale) : ''
      }${toDate ? ` - ${getCustomFormattedDate(getLocalizedDate(toDate, displayedDateFormat, locale), locale)}` : ''}`;
    } else if (flexible && fromDate) {
      // Flexible dates - show month/year format
      displayText = convertToUpperCase(getLocalizedDate(fromDate, displayedDateFormat, locale), locale);
    }

    // Format based on locale (same as getDateText)
    displayText = formatDateStringBasedOnLocale(displayText);

    return displayText;
  }, [
    fromDate,
    toDate,
    flexible,
    readDatesFromStore,
    staticDataContext?.currentLocale,
    staticDataContext?.flexibleDateFormatLabel,
    staticDataContext?.mobileDateFormatLabel,
  ]);

  // CNWEB-6664
  // Extract and reuse the existing condition logic from the desktop implementation.
  // Apply the same logic to the non-desktop view for consistency.
  // Logic explanation: The secondary form should only be hidden when all three fields (Room Guest, Special Rates, and Redeem Points) are disabled.
  const isSecondarySearchBlock = useMemo(() => {
    return (!(model?.disableRoomGuest && model?.disableSpecialRates) || model?.enableRedeemPoints) && !isBulgariDomain;
  }, [model?.disableRoomGuest, model?.disableSpecialRates, model?.enableRedeemPoints, isBulgariDomain]);

  /**
   * checks the rendering view
   */
  const [isClientSideView, setIsClientSideView] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClientSideView(true);
    }
  }, []);

  /**
   * mobile form view dropdown modal state
   */
  const [formModalState, setMobileFormModalState] = useState(false);
  const [isDatesSelected, setIsDatesSelected] = useState(false);
  const [isDestinationParsed, setIsDestinationParsed] = useState('');

  /**
   * set state to display on mobile
   *
   */
  const [currentFormState, setCurrentFormState] = useState<SearchFormActiveState>(SearchFormActiveState.CLOSED_STATE);
  const openMobileModal = (param?: string) => {
    /**
     * open mobile modal with content data
     * it will handle which state we have to open modal
     * it will consist three state one datepicker, desination and complete mobile view
     */
    const str = param ?? currentFormState;
    setMobileFormModalState(true);
    switch (str) {
      case SearchFormActiveState.CLOSED_STATE:
        // Skip destination if hideDestination is true
        setCurrentFormState(
          hideDestination ? SearchFormActiveState.CALENDAR_STATE : SearchFormActiveState.DESTINATION_STATE
        );
        break;
      case SearchFormActiveState.CLOSED_STATE_CALENDAR:
        setCurrentFormState(SearchFormActiveState.CALENDAR_STATE);
        break;
      case SearchFormActiveState.DESTINATION_STATE:
        setCurrentFormState(SearchFormActiveState.CALENDAR_STATE);
        break;
      case SearchFormActiveState.CALENDAR_STATE:
        setCurrentFormState(SearchFormActiveState.FORM_STATE);
        break;
      default:
        setCurrentFormState(SearchFormActiveState.FORM_STATE);
        break;
    }
  };

  const bodyRef = useRef<HTMLBodyElement | null>(null);

  useEffect(() => {
    bodyRef.current = document.body as HTMLBodyElement;
  }, []);

  useEffect(() => {
    if (!isDesktopView) {
      if (formModalState) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
        if (model.isTabbedSearchForm) {
          document.body.style.overflow = 'auto';
        }
        if (model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL) {
          if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) !== 'true') {
            document.body.style.overflow = 'auto';
          }
        }
      }
    } else {
      // Add this else block for desktop view
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) === 'true') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }
  }, [formModalState]);
  const buttonRef = useRef<HTMLButtonElement>(null); //ref to the last element of the search field, to be used in dropdown modal to identify the last element of the modal

  // on client side keeping only desktop or mobile view
  if (isClientSideView) {
    return !isDesktopView ? (
      <StyledSearchFormMobileWrapperDiv
        $isTabbedSearchForm={model.isTabbedSearchForm}
        isNonStickyVariant={isNonStickyVariant}
        tabIndex={0}
        role="tabpanel"
        className={clsx(!model.isTabbedSearchForm ? 'd-block d-lg-none' : '')}
      >
        {/* form view by deafult */}
        <div className="d-flex">
          {model?.isTabbedSearchForm ? (
            <div className={clsx('pt-4', 'tabbed-search-form-container', 'pt-md-4', 'pt-lg-4')}>
              <div className={clsx('mobile-wrapper-default', model?.disableSpecialRates ? 'd-lg-flex' : '')}>
                <SearchPrimaryBlock
                  isButtonHide={true}
                  alertsListArray={model?.alertsListArray}
                  isDesktopView={isDesktopView}
                  submitAction={model?.submitAction}
                  isDatesSelected={isDatesSelected}
                  setCurrentFormState={setCurrentFormState}
                  setMobileFormModalState={setMobileFormModalState}
                  setIsDestinationParsed={setIsDestinationParsed}
                  setIsDatesSelected={setIsDatesSelected}
                  isDestinationParsed={isDestinationParsed}
                  isTabletView={isTabletView}
                  mobileModalHeading={model?.destinationModalHeader}
                  mobilePlaceHolderText={model?.placeholderTextMobile}
                  isMobileForm={true}
                  baselineComponentTitle={model?.baselineComponentTitle}
                  isAvailabilityScenario={isAvailabilityScenario}
                  sessionData={sessionData}
                  blacklist={model?.blacklist}
                />
                <SearchSecondaryBlock
                  isTabletView={isTabletView}
                  isDesktopView={isDesktopView}
                  submitAction={model?.submitAction}
                  isAvailabilityScenario={isAvailabilityScenario}
                  sessionData={sessionData}
                />
                {/* display submit button on mobile view only */}
                {model?.isTabbedSearchForm && (
                  <SearchFormSubmitButton
                    customClass="d-md-none d-sm-block"
                    findHotelLabel={model?.submitCTA}
                    submitAction={model?.submitAction}
                    alertsListArray={model?.alertsListArray}
                    lastElementRef={buttonRef}
                    isAvailabilityScenario={isAvailabilityScenario}
                    sessionData={sessionData}
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              {/* 2 pills layout view by deafult */}
              {!hideDestination && model?.searchCTAEndpointURL !== ADF_SEARCH_CTA_ENDPOINT_URL && (
                <>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`${model?.destinationModuleEyebrowText} ${
                      storeDestinationText || model?.placeholderTextMobile
                    }`}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      openMobileModal(SearchFormActiveState.CLOSED_STATE);
                    }}
                    onKeyDown={event => {
                      if (event.code === '13' || event.key === 'Enter') {
                        openMobileModal(SearchFormActiveState.CLOSED_STATE);
                      }
                    }}
                    data-testid="destination"
                    className={clsx('d-flex', 'destination-input-field')}
                  >
                    <InputTextField
                      classNameForLineType="text-left d-lg-none dashed-line"
                      getInputProps={() => ({
                        readOnly: true,
                        tabIndex: -1,
                      })}
                      label={model?.destinationModuleEyebrowText}
                      iconForLabel="icon-location"
                      placeHolderText={storeDestinationText ? '' : model?.placeholderTextMobile}
                      inputValue={storeDestinationText ?? ''}
                      assignInputValue={true}
                      custom_click_track_value={`${searchFormClickTrackingLoc} | Destination button click |internal`}
                      inputTextFieldClassName={clsx(
                        'custom_click_track',
                        storeDestinationText && 'mobile-view-font-with-value'
                      )}
                      variation="line-type"
                      readOnly={true}
                    />
                  </div>
                  <div className="dashed-separator"></div>
                </>
              )}
              <div
                role="button"
                tabIndex={0}
                aria-label={`${model?.dateModuleEyebrowText} ${mobileDatesDisplayText || model?.dateHelperTextMobile}`}
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  setReadDatesFromStore(true);
                  openMobileModal(SearchFormActiveState.CLOSED_STATE_CALENDAR);
                }}
                onKeyDown={event => {
                  if (event.code === '13' || event.key === 'Enter') {
                    setReadDatesFromStore(true);
                    openMobileModal(SearchFormActiveState.CLOSED_STATE_CALENDAR);
                  }
                }}
                data-testid="dates"
                className={clsx(
                  'dates-input-field',
                  model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL ? 'w-100' : ''
                )}
              >
                <InputTextField
                  classNameForLineType="mobile-homepage-date-field"
                  iconForLabel="icon-stay-dates"
                  getInputProps={() => ({
                    readOnly: true,
                    tabIndex: -1,
                  })}
                  label={model?.dateModuleEyebrowText}
                  placeHolderText={mobileDatesDisplayText ? '' : model?.dateHelperTextMobile}
                  inputValue={mobileDatesDisplayText}
                  assignInputValue={true}
                  custom_click_track_value={`${searchFormClickTrackingLoc} | Date Picker button click |internal`}
                  inputTextFieldClassName={clsx(
                    'custom_click_track',
                    mobileDatesDisplayText && 'mobile-view-font-with-value'
                  )}
                  variation="line-type"
                  readOnly={true}
                ></InputTextField>
              </div>
              {model?.showEditCTAOnMobile && (
                <div className={clsx('p-3', 'd-flex', 'align-items-center', 'justify-content-end')}>
                  <button
                    className={clsx('m-button-s', 'm-button-secondary', 'search-form-edit-button')}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      openMobileModal(SearchFormActiveState.FORM_STATE);
                    }}
                    onKeyDown={event => {
                      if (event.code === '13' || event.key === 'Enter') {
                        openMobileModal(SearchFormActiveState.FORM_STATE);
                      }
                    }}
                  >
                    <span className={clsx({ nowrap: isGreaterChinaLocale(currentLocale) }, 'edit-cta-content')}>
                      {model?.editCTALabel}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {props.enableConversationalAISearch && !props.isSticky && (
          <div className={clsx(isDesktopView ? 'px-5 mx-2' : 'px-2')}>
            <ConversationalAISearchBanner />
          </div>
        )}

        <MobileSearchFormContent
          isTabbedSearchForm={model.isTabbedSearchForm}
          contentType={currentFormState}
          isDatesSelected={isDatesSelected}
          isDestinationParsed={isDestinationParsed}
          isDesktopView={isDesktopView}
          isTabletView={isTabletView}
          formModalState={formModalState}
          isSecondarySearchBlock={isSecondarySearchBlock}
          isAvailabilityScenario={isAvailabilityScenario}
          hideDestination={hideDestination}
          model={model}
          sessionData={sessionData}
          setCurrentFormState={setCurrentFormState}
          setMobileFormModalState={setMobileFormModalState}
          setIsDestinationParsed={setIsDestinationParsed}
          setIsDatesSelected={setIsDatesSelected}
          setIsWeekendButtonSelected={setIsWeekendButtonSelected}
          isWeekendButtonSelected={isWeekendButtonSelected}
          bodyRef={bodyRef}
          buttonRef={buttonRef}
          setReadDatesFromStore={setReadDatesFromStore}
        />
      </StyledSearchFormMobileWrapperDiv>
    ) : (
      <StyledSearchFormDesktopWrapperDiv
        $isTabbedSearchForm={model.isTabbedSearchForm}
        className={clsx(
          model?.isTabbedSearchForm ? 'pt-lg-4 pt-0' : '',
          !model.isTabbedSearchForm ? 'd-none d-lg-block' : '',
          isAvailabilityScenario ? 'availibilty-search-container d-flex flex-wrap' : ''
        )}
      >
        <SearchPrimaryBlock
          findHotelLabel={model?.findHotelLabel}
          alertsListArray={model?.alertsListArray}
          isButtonHide={false}
          isDesktopView={isDesktopView}
          submitAction={model?.submitAction}
          isTabletView={isTabletView}
          mobileModalHeading={
            model?.isTabbedSearchForm ? model?.destinationModalHeader : model?.expandedPlaceHolderTextMobile
          }
          mobilePlaceHolderText={model?.placeholderTextMobile}
          isSearchFormSticky={props.isSearchFormSticky}
          baselineComponentTitle={model?.baselineComponentTitle}
          isAvailabilityScenario={isAvailabilityScenario}
          sessionData={sessionData}
          blacklist={model?.blacklist}
          destinationColStyleOverride={props.destinationColStyleOverride}
          isSticky={props.isSticky}
          enableConversationalAISearch={props.enableConversationalAISearch}
        />

        {isSecondarySearchBlock && (
          <SearchSecondaryBlock
            submitAction={model?.submitAction}
            isTabletView={isTabletView}
            isDesktopView={isDesktopView}
            isAvailabilityScenario={isAvailabilityScenario}
            sessionData={sessionData}
          />
        )}
        {model?.isTabbedSearchForm !== true && hideDestination !== true ? (
          <SearchFormSubmitButton
            findHotelLabel={model?.findHotelLabel}
            submitAction={model?.submitAction}
            customClass={`d-none d-lg-block searchfrom-submit-btn ${
              isGreaterChinaLocale(currentLocale) ? 'cn-searchfrom-submit-btn' : ''
            }`}
            sessionData={sessionData}
            isAvailabilityScenario={isAvailabilityScenario}
          />
        ) : (
          ''
        )}
        {model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL && hideDestination ? (
          <SearchFormSubmitButton
            findHotelLabel={model?.findHotelLabel}
            submitAction={model?.submitAction}
            customClass={`d-none d-lg-block searchfrom-submit-btn ${
              isGreaterChinaLocale(currentLocale) ? 'cn-searchfrom-submit-btn' : ''
            }`}
            sessionData={sessionData}
            isAvailabilityScenario={isAvailabilityScenario}
            setMobileFormModalState={setMobileFormModalState}
          />
        ) : (
          ''
        )}
        {props.enableConversationalAISearch && !props.isSticky && (
          <div className={clsx(isDesktopView ? 'px-5 mx-2' : 'px-2')}>
            <ConversationalAISearchBanner />
          </div>
        )}
      </StyledSearchFormDesktopWrapperDiv>
    );
  }
  return (
    <div>
      <div className="d-none d-lg-block">
        <StyledSearchFormDesktopWrapperDiv
          $isTabbedSearchForm={model.isTabbedSearchForm}
          className={clsx(
            model?.isTabbedSearchForm ? 'pt-lg-4 pt-0' : '',
            !model.isTabbedSearchForm ? 'd-none d-lg-block' : '',
            isAvailabilityScenario ? 'availibilty-search-container d-flex flex-wrap' : ''
          )}
        >
          <SearchPrimaryBlock
            findHotelLabel={model?.findHotelLabel}
            alertsListArray={model?.alertsListArray}
            isButtonHide={false}
            isDesktopView={isDesktopView}
            submitAction={model?.submitAction}
            isTabletView={isTabletView}
            mobileModalHeading={
              model?.isTabbedSearchForm ? model?.destinationModalHeader : model?.expandedPlaceHolderTextMobile
            }
            mobilePlaceHolderText={model?.placeholderTextMobile}
            isSearchFormSticky={props.isSearchFormSticky}
            baselineComponentTitle={model?.baselineComponentTitle}
            isAvailabilityScenario={isAvailabilityScenario}
            sessionData={sessionData}
            blacklist={model?.blacklist}
          />

          {isSecondarySearchBlock && (
            <SearchSecondaryBlock
              submitAction={model?.submitAction}
              isTabletView={isTabletView}
              isDesktopView={isDesktopView}
              isAvailabilityScenario={isAvailabilityScenario}
              sessionData={sessionData}
            />
          )}
          {model?.isTabbedSearchForm !== true && hideDestination !== true ? (
            <SearchFormSubmitButton
              findHotelLabel={model?.findHotelLabel}
              submitAction={model?.submitAction}
              customClass={`d-none d-lg-block searchfrom-submit-btn ${
                isGreaterChinaLocale(currentLocale) ? 'cn-searchfrom-submit-btn' : ''
              }`}
              sessionData={sessionData}
              isAvailabilityScenario={isAvailabilityScenario}
            />
          ) : (
            ''
          )}
        </StyledSearchFormDesktopWrapperDiv>
      </div>
      <div className="d-block d-lg-none">
        <StyledSearchFormMobileWrapperDiv
          $isTabbedSearchForm={model.isTabbedSearchForm}
          isNonStickyVariant={isNonStickyVariant}
          tabIndex={0}
          role="tabpanel"
          className={clsx(!model.isTabbedSearchForm ? 'd-flex d-lg-none' : '')}
        >
          {/* form view by deafult */}
          {model?.isTabbedSearchForm ? (
            <div className="pt-4 tabbed-search-form-container pt-md-4 pt-lg-4 ">
              <div className={clsx('mobile-wrapper-default')}>
                <SearchPrimaryBlock
                  isButtonHide={true}
                  alertsListArray={model?.alertsListArray}
                  isDesktopView={isDesktopView}
                  submitAction={model?.submitAction}
                  isDatesSelected={isDatesSelected}
                  setCurrentFormState={setCurrentFormState}
                  setMobileFormModalState={setMobileFormModalState}
                  setIsDestinationParsed={setIsDestinationParsed}
                  setIsDatesSelected={setIsDatesSelected}
                  isDestinationParsed={isDestinationParsed}
                  isTabletView={isTabletView}
                  mobileModalHeading={model?.destinationModalHeader}
                  mobilePlaceHolderText={model?.placeholderTextMobile}
                  isMobileForm={true}
                  baselineComponentTitle={model?.baselineComponentTitle}
                  isAvailabilityScenario={isAvailabilityScenario}
                  sessionData={sessionData}
                  blacklist={model?.blacklist}
                />
                <SearchSecondaryBlock
                  isTabletView={isTabletView}
                  isDesktopView={isDesktopView}
                  submitAction={model?.submitAction}
                  isAvailabilityScenario={isAvailabilityScenario}
                  sessionData={sessionData}
                />
                {/* display submit button on mobile view only */}
                {model?.isTabbedSearchForm && (
                  <SearchFormSubmitButton
                    customClass="d-md-none d-sm-block"
                    findHotelLabel={model?.submitCTA}
                    submitAction={model?.submitAction}
                    alertsListArray={model?.alertsListArray}
                    lastElementRef={buttonRef}
                    isAvailabilityScenario={isAvailabilityScenario}
                    sessionData={sessionData}
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              {/* 2 pills layout view by deafult */}
              {!hideDestination && model?.searchCTAEndpointURL !== ADF_SEARCH_CTA_ENDPOINT_URL && (
                <>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`${model?.destinationModuleEyebrowText} ${
                      storeDestinationText || model?.placeholderTextMobile
                    }`}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      openMobileModal(SearchFormActiveState.CLOSED_STATE);
                    }}
                    onKeyDown={event => {
                      if (event.code === '13' || event.key === 'Enter') {
                        openMobileModal(SearchFormActiveState.CLOSED_STATE);
                      }
                    }}
                    data-testid="destination"
                    className={clsx('d-flex', 'destination-input-field')}
                  >
                    <InputTextField
                      classNameForLineType="text-left d-lg-none dashed-line"
                      getInputProps={() => ({
                        readOnly: true,
                        tabIndex: -1,
                      })}
                      label={model?.destinationModuleEyebrowText}
                      iconForLabel="icon-location"
                      placeHolderText={storeDestinationText ? '' : model?.placeholderTextMobile}
                      inputValue={storeDestinationText ?? ''}
                      assignInputValue={true}
                      custom_click_track_value={`${searchFormClickTrackingLoc} | Destination button click |internal`}
                      inputTextFieldClassName={clsx(
                        'custom_click_track',
                        storeDestinationText && 'mobile-view-font-with-value'
                      )}
                      variation="line-type"
                      readOnly={true}
                    />
                  </div>
                  <div className="dashed-separator"></div>
                </>
              )}
              <div
                role="button"
                tabIndex={0}
                aria-label={`${model?.dateModuleEyebrowText} ${mobileDatesDisplayText || model?.dateHelperTextMobile}`}
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  setReadDatesFromStore(true);
                  openMobileModal(SearchFormActiveState.CLOSED_STATE_CALENDAR);
                }}
                onKeyDown={event => {
                  if (event.code === '13' || event.key === 'Enter') {
                    setReadDatesFromStore(true);
                    openMobileModal(SearchFormActiveState.CLOSED_STATE_CALENDAR);
                  }
                }}
                data-testid="dates"
                className={clsx(
                  'dates-input-field',
                  model?.searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL ? 'w-100' : ''
                )}
              >
                <InputTextField
                  classNameForLineType="mobile-homepage-date-field"
                  iconForLabel="icon-stay-dates"
                  getInputProps={() => ({
                    readOnly: true,
                    tabIndex: -1,
                  })}
                  label={model?.dateModuleEyebrowText}
                  placeHolderText={mobileDatesDisplayText ? '' : model?.dateHelperTextMobile}
                  inputValue={mobileDatesDisplayText}
                  assignInputValue={true}
                  custom_click_track_value={`${searchFormClickTrackingLoc} | Date Picker button click |internal`}
                  inputTextFieldClassName={clsx(
                    'custom_click_track',
                    mobileDatesDisplayText && 'mobile-view-font-with-value'
                  )}
                  variation="line-type"
                  readOnly={true}
                ></InputTextField>
              </div>
              {model?.showEditCTAOnMobile && (
                <div className={clsx('p-3', 'd-flex', 'align-items-center', 'justify-content-end')}>
                  <button
                    className={clsx('m-button-s', 'm-button-secondary', 'search-form-edit-button')}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      openMobileModal(SearchFormActiveState.FORM_STATE);
                    }}
                    onKeyDown={event => {
                      if (event.code === '13' || event.key === 'Enter') {
                        openMobileModal(SearchFormActiveState.FORM_STATE);
                      }
                    }}
                  >
                    <span className={clsx({ nowrap: isGreaterChinaLocale(currentLocale) }, 'edit-cta-content')}>
                      {model?.editCTALabel}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}

          <MobileSearchFormContent
            isTabbedSearchForm={model.isTabbedSearchForm}
            contentType={currentFormState}
            isDatesSelected={isDatesSelected}
            isDestinationParsed={isDestinationParsed}
            isDesktopView={isDesktopView}
            isTabletView={isTabletView}
            formModalState={formModalState}
            isSecondarySearchBlock={isSecondarySearchBlock}
            isAvailabilityScenario={isAvailabilityScenario}
            hideDestination={hideDestination}
            model={model}
            sessionData={sessionData}
            setCurrentFormState={setCurrentFormState}
            setMobileFormModalState={setMobileFormModalState}
            setIsDestinationParsed={setIsDestinationParsed}
            setIsDatesSelected={setIsDatesSelected}
            setIsWeekendButtonSelected={setIsWeekendButtonSelected}
            isWeekendButtonSelected={isWeekendButtonSelected}
            bodyRef={bodyRef}
            buttonRef={buttonRef}
            setReadDatesFromStore={setReadDatesFromStore}
          />
        </StyledSearchFormMobileWrapperDiv>
      </div>
    </div>
  );
});
